import React from "react";
import { Link } from "react-router-dom";
import SectionHeading from "../components/SectionHeading";
import SEO from "../components/SEO";
import Accreditations from "../components/Accreditations";

const HERO = "/brand/consulting-room.jpg";

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO title="About Our Cambridge Clinic" description="A private, consultant-led clinic in Cambridge for blood pressure, heart and metabolic health - consultant physicians affiliated with Cambridge University Hospitals NHS Foundation Trust." path="/about" />
      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="eyebrow">About the clinic · Consultant-led hypertension &amp; cardiovascular prevention</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02]">A specialist clinic, with time to listen.</h1>
            <p className="mt-6 text-ink-muted text-lg leading-relaxed">
              Cambridge Cardiovascular Prevention Clinic was set up to give patients something simple: time with a senior consultant, a thorough look at what’s actually going on, and a clear plan to act on. Led by consultants with specialist expertise in high blood pressure and cardiovascular prevention — Professor Ian Wilkinson, one of our consultants, is Immediate Past-President of the British and Irish Hypertension Society and a co-author of the UK national guidelines that GPs use today.
            </p>
            <p data-testid="about-cuh-notice" className="mt-4 text-ink-muted">
              Consultations take place at Cambridge University Hospitals NHS Foundation Trust.
            </p>
          </div>
          <div className="lg:col-span-6">
            <figure>
              <div className="aspect-[4/3] overflow-hidden rounded-md border border-bronze-light/30 shadow-sm">
                <img
                  src={HERO}
                  alt="Illustrative image of a calm, modern seating area"
                  className="w-full h-full object-cover"
                />
              </div>
              <figcaption data-testid="about-image-caption" className="mt-2 text-xs text-ink-muted">Illustrative image, not our premises.</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="container-ccc py-20">
        <SectionHeading eyebrow="How we work" title="Calm, caring and on your side." />
        <div className="mt-12 grid md:grid-cols-3 gap-8">
          {[
            { t: "We make time", d: "Your first appointment is a full hour. We listen properly, examine you carefully, and explain everything in plain English." },
            { t: "We get the right tests", d: "We can arrange the scans, monitors and blood tests you need — but only when they’ll genuinely help." },
            { t: "We talk to your GP", d: "Whatever we agree, we write down clearly and send to your GP, so your everyday care stays joined up." },
          ].map((b) => (
            <div key={b.t} className="p-8 bg-white border border-stone-200/60 rounded-md">
              <h3 className="font-serif text-2xl">{b.t}</h3>
              <p className="mt-3 text-sm text-ink-muted leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="about-research" className="bg-cream-100 border-y border-bronze-light/30">
        <div className="container-ccc py-20 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow">Research-active clinic</div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-4 leading-tight">A clinic shaped by the research it leads.</h2>
            <p className="mt-5 text-ink-muted leading-relaxed">
              Two of our consultants run internationally recognised cardiovascular research programmes in Cambridge. That matters for patients: the care you receive here is informed by — and often ahead of — current guidelines.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <div className="p-6 bg-white border-l-2 border-bronze-dark rounded-md">
              <h3 className="font-serif text-xl">Professor Ian Wilkinson</h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                High-impact publications in hypertension across journals including The Lancet, JAMA, Circulation and the European Heart Journal. Co-author of UK and European hypertension guidelines.
              </p>
            </div>
            <div className="p-6 bg-white border-l-2 border-bronze-dark rounded-md">
              <h3 className="font-serif text-xl">Dr Joseph Cheriyan</h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                Director of the Cardiovascular Trials Office at the Cambridge Clinical Trials Unit. Active research and high-impact publications in hypertension, hypercholesterolaemia, heart failure and acute coronary syndromes — including in Nature Medicine, Circulation and Hypertension — alongside numerous first-in-human trials of new cardiovascular medicines.
              </p>
            </div>
            <p className="text-xs text-ink-muted/80">
              Patients are never enrolled in research without their explicit consent. Our consulting practice and any research participation are kept entirely separate.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-ink text-cream-50">
        <div className="container-ccc py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow text-bronze-light">Safety & standards</div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-4 leading-tight">The same standards you’d expect from the NHS.</h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-cream-100/85 text-base leading-relaxed">
            <p>Our consultants are all senior NHS doctors at Cambridge University Hospitals. They are on the GMC specialist register, fully insured, and revalidated regularly — exactly as in the NHS.</p>
            <p>We are internationally accredited: an <strong className="text-cream-50">ESH-accredited Hypertension Specialist Centre</strong> and an <strong className="text-cream-50">EACVI-accredited echocardiography centre</strong> — recognition reserved for centres meeting strict European clinical and quality standards.</p>
            <p>Your private records are kept private. We only ever share information with your GP or other doctors with your permission, in line with UK GDPR.</p>
            <div className="pt-4">
              <Accreditations />
            </div>
          </div>
        </div>
      </section>

      <section className="container-ccc py-20 text-center">
        <SectionHeading center title="Want to chat before you book?" subtitle="If you’re not sure whether we can help, just get in touch — we’ll happily talk it through with you." />
        <div className="mt-10 flex justify-center gap-4">
          <Link data-testid="about-enquiry-cta" to="/enquiry" className="btn-primary">Book an appointment</Link>
          <Link data-testid="about-contact-cta" to="/contact" className="btn-secondary">Contact the team</Link>
        </div>
      </section>
    </div>
  );
}
